import { ApiParams, FetchError } from '../../common/api/api';
import { Attachment } from '../../common/attachment/Attachment.interface';
import { FormikSelectOptions } from '../../common/formik/FormikSelectField';
import { User } from '../../user/User.interface';
import { Task } from '../tasks/Task.interface';
import { TaskTypeEntity } from '../task-types/TaskType.interface';
import { FileDto } from '../documents/Document.interface';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import { Hazard } from '../hazards/Hazard.interface';

export interface Product {
  id: number;
  taskId: number;
  task: Task;
  name: string;
  description: string;
  identifier: string;
  regionsMarketed: string;
  intendedUse: string;
  version: string;
  productOwner: User;
  techLead: User;
  templatePosition?: { id: number; name: string }[];
  created: Date;
  createdBy: User;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}

export enum ProductRisk {
  'Very Low' = 1,
  Low = 2,
  Medium = 3,
  High = 4,
  Critical = 5
}

export const productRiskOptions: FormikSelectOptions[] = Object.entries(
  ProductRisk
).reduce((options: FormikSelectOptions[], [key, value]) => {
  if (typeof value === 'number') {
    options.push({ value, label: `${value} - ${key}` });
  }
  return options;
}, []);

export interface ProductUserNeed {
  id: number;
  title: string;
  description: string;
  risk: ProductRisk;
  productId: number;
  taskId: number;
  task: Task;
  releaseId: number;
  release: ProductRelease;
  components: ProductComponent[];
  designInputs?: ProductDesignInput[];
  validations?: ProductValidation[];
  designSpecs?: ProductDesignSpec[];
  userId: number;
  user: User;
  created: Date;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}

export interface ProductUserNeedVersion {
  id: number;
  version: number;
  title: string;
  description: string;
  risk: ProductRisk;
  releaseId: number;
  release: ProductRelease;
  components: Partial<ProductComponent>[];
  attachments?: Attachment[];
  designInputIds: number[];
  validationIds: number[];
  designSpecIds: number[];
  created: Date;
  task?: Task;
}

export interface ProductDesignInput {
  id: number;
  title: string;
  essentialRequirements: string;
  description: string;
  verificationMethod: string;
  whenToVerify: string;
  risk: ProductRisk;
  productId: number;
  taskId: number;
  task: Task;
  releaseId: number;
  release: ProductRelease;
  components: ProductComponent[];
  designOutputs?: ProductDesignOutput[];
  validations?: ProductValidation[];
  userId: number;
  user: User;
  created: Date;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}

export interface ProductDesignInputVersion {
  id: number;
  version: number;
  title: string;
  essentialRequirements: string;
  description: string;
  verificationMethod: string;
  whenToVerify: string;
  risk: ProductRisk;
  releaseId: number;
  release: ProductRelease;
  components: ProductComponent[];
  attachments?: Attachment[];
  userNeedIds: number[];
  designOutputIds: number[];
  created: Date;
  task?: Task;
}

export interface ProductDesignOutput {
  id: number;
  title: string;
  essentialRequirements: string;
  description: string;
  productId: number;
  taskId: number;
  task: Task;
  releaseId: number;
  release: ProductRelease;
  relatedHazard: string;
  hazardSituation: string;
  potentialHarm: string;
  potentialCauses: string;
  relevance: string[];
  currentControls: string;
  recommendedActions: string;
  actionsTaken: string;
  verification: string;
  p1: number;
  s1: number;
  d1: number;
  p2: number;
  s2: number;
  d2: number;
  rpn1: number;
  rpn2: number;
  components: ProductComponent[];
  verifications?: ProductVerification[];
  validations?: ProductValidation[];
  userId: number;
  user: User;
  created: Date;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}

export interface ProductDesignOutputVersion {
  id: number;
  version: number;
  title: string;
  essentialRequirements: string;
  description: string;
  releaseId: number;
  release: ProductRelease;
  components: ProductComponent[];
  attachments?: Attachment[];
  designInputIds: number[];
  verificationIds: number[];
  created: Date;
  task?: Task;
}

export interface ProductVerification {
  id: number;
  title: string;
  description: string;
  productId: number;
  taskId: number;
  task: Task;
  releaseId: number;
  release: ProductRelease;
  components: ProductComponent[];
  userId: number;
  user: User;
  created: Date;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}

export interface ProductVerificationVersion {
  id: number;
  version: number;
  title: string;
  description: string;
  releaseId: number;
  release: ProductRelease;
  components: ProductComponent[];
  attachments?: Attachment[];
  designOutputIds: number[];
  created: Date;
  task?: Task;
}

export interface ProductValidation {
  id: number;
  title: string;
  description: string;
  productId: number;
  taskId: number;
  task: Task;
  releaseId: number;
  release: ProductRelease;
  components: ProductComponent[];
  userId: number;
  user: User;
  created: Date;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}

export interface ProductValidationVersion {
  id: number;
  version: number;
  title: string;
  description: string;
  releaseId: number;
  release: ProductRelease;
  components: ProductComponent[];
  attachments?: Attachment[];
  userNeedIds: number[];
  created: Date;
  task?: Task;
}

export interface ProductDesignSpec {
  id: number;
  title: string;
  description: string;
  productId: number;
  taskId: number;
  task: Task;
  releaseId: number;
  release: ProductRelease;
  components: ProductComponent[];
  userId: number;
  user: User;
  created: Date;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}

export interface ProductDesignSpecVersion {
  id: number;
  version: number;
  title: string;
  description: string;
  releaseId: number;
  release: ProductRelease;
  components: ProductComponent[];
  attachments?: Attachment[];
  userNeedIds: number[];
  created: Date;
  task?: Task;
}

export interface ProductTemplate {
  id: number;
  name: string;
  type: ProductExportEntity;
  productId: number;
  product: Product;
  fileId?: number | null;
  file?: FileDto;
  created: Date;
  createdBy: User;
  modified: Date;
  modifiedBy: User;
}

export type ProductEntity =
  | ProductUserNeed
  | ProductDesignInput
  | ProductDesignOutput
  | ProductVerification
  | ProductValidation
  | ProductDesignSpec
  | Hazard;

export interface ProductRelationMap {
  from: string;
  to: string;
}

export const ProductWorkflowEntities = [
  WorkflowEntity.UserNeed,
  WorkflowEntity.DesignInput,
  WorkflowEntity.DesignOutput,
  WorkflowEntity.DesignVerification,
  WorkflowEntity.DesignValidation,
  WorkflowEntity.DesignSpec,
  WorkflowEntity.Hazard
];

export const ProductTaskTypeEntities: TaskTypeEntity[] = [
  TaskTypeEntity.UserNeed,
  TaskTypeEntity.DesignInput,
  TaskTypeEntity.DesignOutput,
  TaskTypeEntity.DesignVerification,
  TaskTypeEntity.DesignValidation,
  TaskTypeEntity.DesignSpec
];

export enum ProductExportEntity {
  UserNeed = 'UserNeed',
  DesignInput = 'DesignInput',
  DesignOutput = 'DesignOutput',
  DesignSpec = 'DesignSpec',
  Verification = 'Verification',
  Validation = 'Validation',
  TraceabilityReport = 'TraceabilityReport',
  eClinicalReport = 'eClinicalReport',
  Hazard = 'Hazard'
}

type EditableCardProps<
  ValueType,
  ValueVersionType,
  ValueKey extends string = never,
  ValueVersionKey extends string = never,
  ExtraProps extends object = {}
> = ExtraProps & { [key in ValueKey]: ValueType } & {
  [key in ValueVersionKey]?: ValueVersionType;
} & {
  title?: string;
  onChange?: (value: Partial<ValueType>) => void;
  onUpdate?: () => void;
  onError?: (error: FetchError) => void;
};

export interface ProductCardCommonProps {
  productId: number;
  hidden?: boolean;
  enableTitleRedirect?: boolean;
}

export type BaseProductDesignInputCardProps = EditableCardProps<
  ProductDesignInput,
  ProductDesignInputVersion,
  'productDesignInput',
  'productDesignInputVersion',
  ProductCardCommonProps
>;

export type BaseProductDesignOutputCardProps = EditableCardProps<
  ProductDesignOutput,
  ProductDesignOutputVersion,
  'productDesignOutput',
  'productDesignOutputVersion',
  ProductCardCommonProps
>;

export type BaseProductDesignSpecCardProps = EditableCardProps<
  ProductDesignSpec,
  ProductDesignSpecVersion,
  'productDesignSpec',
  'productDesignSpecVersion',
  ProductCardCommonProps
>;

export type BaseProductVerificationCardProps = EditableCardProps<
  ProductVerification,
  ProductVerificationVersion,
  'productVerification',
  'productVerificationVersion',
  ProductCardCommonProps
>;

export type BaseProductUserNeedsCardProps = EditableCardProps<
  ProductUserNeed,
  ProductUserNeedVersion,
  'productUserNeed',
  'productUserNeedVersion',
  ProductCardCommonProps
>;

export type BaseProductValidationCardProps = EditableCardProps<
  ProductValidation,
  ProductValidationVersion,
  'productValidation',
  'productValidationVersion',
  ProductCardCommonProps
>;

export type BaseProductAssignDialogProps<
  ValueID extends string = never,
  SelectedItemType = any,
  AssignedItemsType = any
> = { [key in ValueID]: number } & {
  productId: number;
  open: boolean;
  onCancel?: () => void;
  onAssigned?: (items: AssignedItemsType) => void;
  onError?: (error: FetchError) => void;
  selectedItems?: SelectedItemType[];
};

export type BaseProductBoardColumnProps<
  ProductEntityType extends ProductEntity,
  ValueKey extends string = never
> = { [key in ValueKey]: ProductEntityType | Partial<ProductEntityType> } & {
  product: Product;
  collapsed?: boolean;
  onCancel?: () => void;
  onError?: () => void;
  onSave?: (value: ProductEntityType, created?: boolean) => void;
};

export interface BaseProductEntityTableProps {
  productId: number;
  apiParams: ApiParams;
  fetchRef?: React.MutableRefObject<Function | undefined>;
}

export interface ProductComponent {
  id: number;
  name: string;
  productId: number;
  created: Date;
  modified: Date;
}

export interface ProductRelease {
  id: number;
  name: string;
  description?: string;
  productId: number;
  created: Date;
  modified: Date;
}

export interface ProductBoardData {
  productId: number;
  referencedProductId?: number;
  referencedProductTitle?: string;
  userNeeds: ProductUserNeed[];
  designInputs: ProductDesignInput[];
  designOutputs: ProductDesignOutput[];
  verifications: ProductVerification[];
  validations: ProductValidation[];
  designSpecs: ProductDesignSpec[];
}
